import { baseApi } from '@krp/client-runtime'

import {
  CATALOG_SOURCE,
  DIVERGENCE_PAGE_SIZE,
  PAGE_SIZE,
  type ApplyCommand,
  type ApplyJob,
  type ChangeType,
  type CheckRun,
  type CreateTemplate,
  type DataKind,
  type DivergenceCount,
  type DivergencesPage,
  type Ignore,
  type IgnoreCommand,
  type InitProposal,
  type InitResult,
  type JournalPage,
  type PriceMapsView,
  type PriceTypeMap,
  type PriceVatRuleItem,
  type Schedule,
  type SourcesSummary,
} from '../model/types'
import {
  decodeCheckRun,
  decodeCount,
  decodeDivergences,
  decodeIgnore,
  decodeIgnores,
  decodeInit,
  decodeInitResult,
  decodeJob,
  decodeJournal,
  decodePriceMaps,
  decodeSchedule,
  decodeSummary,
  decodeTemplate,
} from './contracts'
import { SYNC_URLS } from './urls'

/**
 * Служба синхронизации в общем `baseApi`. Команды не повторяются transport-ом (ADR-11): каждая несёт `operationId` намерения
 * человека, повтор тем же ключом служба узнаёт сама. Данные сервера живут только в этом кэше.
 */
export const SYNC_TAG = 'Sync' as const

const SUMMARY = { type: SYNC_TAG, id: 'SUMMARY' } as const
const DIVERGENCES = { type: SYNC_TAG, id: 'DIVERGENCES' } as const
const COUNT = { type: SYNC_TAG, id: 'COUNT' } as const
const IGNORES = { type: SYNC_TAG, id: 'IGNORES' } as const
const PRICE_MAPS = { type: SYNC_TAG, id: 'PRICE_MAPS' } as const
const SCHEDULE = { type: SYNC_TAG, id: 'SCHEDULE' } as const
const JOURNAL = { type: SYNC_TAG, id: 'JOURNAL' } as const
const initTag = (resourceId: string) => ({ type: SYNC_TAG, id: `INIT:${resourceId}` }) as const

/** Всё, что меняется после применения, «не отслеживать» или новых связей: сводка, списки, счётчик, журнал. */
const AFTER_CHANGE = [SUMMARY, DIVERGENCES, COUNT, JOURNAL]

export interface DivergencesArgs {
  /** Ресурс или `catalog` — общий каталог (позиции без связей, без источника в запросе). */
  source: string
  type: ChangeType | null
  dataKind: DataKind | null
  query: string
  page: number
}

export interface CheckArgs {
  resourceId: string
  operationId: string
}

export interface InitConfirmArgs {
  resourceId: string
  operationId: string
  pairs: { productId: string; remoteKey: string }[]
}

export const syncApi = baseApi.enhanceEndpoints({ addTagTypes: [SYNC_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    syncSummary: build.query<SourcesSummary, void>({
      query: () => ({ url: SYNC_URLS.summary, decode: decodeSummary }),
      providesTags: [SUMMARY],
    }),
    syncDivergences: build.query<DivergencesPage, DivergencesArgs>({
      query: ({ source, type, dataKind, query, page }) => ({
        url: SYNC_URLS.divergences,
        params: {
          source: source === CATALOG_SOURCE ? null : source,
          type: source === CATALOG_SOURCE ? 'only-common' : type,
          kind: dataKind,
          q: query.trim() === '' ? null : query.trim(),
          page,
          pageSize: DIVERGENCE_PAGE_SIZE,
        },
        decode: decodeDivergences,
      }),
      providesTags: [DIVERGENCES],
    }),
    syncDivergenceCount: build.query<DivergenceCount, void>({
      query: () => ({ url: SYNC_URLS.divergenceCount, decode: decodeCount }),
      providesTags: [COUNT],
    }),
    syncCreateTemplate: build.query<CreateTemplate, { resourceId: string }>({
      query: ({ resourceId }) => ({ url: SYNC_URLS.createTemplate(resourceId), decode: decodeTemplate }),
    }),
    syncApply: build.mutation<ApplyJob, ApplyCommand>({
      query: (command) => ({ url: SYNC_URLS.apply, method: 'POST', body: command, idempotent: true, decode: decodeJob }),
      invalidatesTags: (_result, error) => (error ? [] : [JOURNAL]),
    }),
    /** Повтор только неудавшихся строк задачи — новой задачей; исход неизвестных строк служба сначала спросит по прежнему ключу. */
    syncApplyRetry: build.mutation<ApplyJob, { jobId: string; operationId: string }>({
      query: ({ jobId, operationId }) => ({ url: SYNC_URLS.applyJobRetry(jobId), method: 'POST', body: { operationId }, idempotent: true, decode: decodeJob }),
      invalidatesTags: (_result, error) => (error ? [] : [JOURNAL]),
    }),
    syncJob: build.query<ApplyJob, { jobId: string }>({
      query: ({ jobId }) => ({ url: SYNC_URLS.applyJob(jobId), decode: decodeJob }),
    }),
    syncIgnores: build.query<Ignore[], void>({
      query: () => ({ url: SYNC_URLS.ignores, decode: decodeIgnores }),
      providesTags: [IGNORES],
    }),
    syncIgnore: build.mutation<Ignore, IgnoreCommand>({
      query: (command) => ({ url: SYNC_URLS.ignores, method: 'POST', body: command, idempotent: true, decode: decodeIgnore }),
      invalidatesTags: (_result, error) => (error ? [] : [...AFTER_CHANGE, IGNORES]),
    }),
    syncUnignore: build.mutation<unknown, { id: number }>({
      query: ({ id }) => ({ url: SYNC_URLS.ignore(id), method: 'DELETE' }),
      invalidatesTags: (_result, error) => (error ? [] : [...AFTER_CHANGE, IGNORES]),
    }),
    syncInit: build.query<InitProposal, { resourceId: string }>({
      query: ({ resourceId }) => ({ url: SYNC_URLS.init(resourceId), decode: decodeInit }),
      providesTags: (_result, _error, { resourceId }) => [initTag(resourceId)],
    }),
    syncInitConfirm: build.mutation<InitResult, InitConfirmArgs>({
      query: ({ resourceId, operationId, pairs }) => ({
        url: SYNC_URLS.init(resourceId),
        method: 'POST',
        body: { operationId, pairs },
        idempotent: true,
        decode: decodeInitResult,
      }),
      invalidatesTags: (_result, error, { resourceId }) => (error ? [] : [...AFTER_CHANGE, initTag(resourceId)]),
    }),
    syncPriceMaps: build.query<PriceMapsView, void>({
      query: () => ({ url: SYNC_URLS.priceTypeMaps, decode: decodePriceMaps }),
      providesTags: [PRICE_MAPS],
    }),
    /** Замена сопоставления целиком: то, что человек видит в окне, и есть новое состояние. */
    syncPriceMapsSave: build.mutation<PriceMapsView, { items: PriceTypeMap[] }>({
      query: (body) => ({ url: SYNC_URLS.priceTypeMaps, method: 'PUT', body, decode: decodePriceMaps }),
      invalidatesTags: (_result, error) => (error ? [] : [...AFTER_CHANGE, PRICE_MAPS]),
    }),
    syncPriceRulesSave: build.mutation<unknown, { items: PriceVatRuleItem[] }>({
      query: (body) => ({ url: SYNC_URLS.priceVatRules, method: 'PUT', body }),
      invalidatesTags: (_result, error) => (error ? [] : AFTER_CHANGE),
    }),
    syncCheck: build.mutation<CheckRun, { resourceId: string; operationId: string }>({
      query: ({ resourceId, operationId }) => ({ url: SYNC_URLS.check(resourceId), method: 'POST', body: { operationId }, idempotent: true, decode: decodeCheckRun }),
      invalidatesTags: (_result, error) => (error ? [] : [SUMMARY]),
    }),
    syncSchedule: build.query<Schedule, void>({
      query: () => ({ url: SYNC_URLS.schedule, decode: decodeSchedule }),
      providesTags: [SCHEDULE],
    }),
    syncScheduleSave: build.mutation<Schedule, { nightlyAt: string }>({
      query: (body) => ({ url: SYNC_URLS.schedule, method: 'PUT', body, decode: decodeSchedule }),
      invalidatesTags: (_result, error) => (error ? [] : [SCHEDULE]),
    }),
    syncJournal: build.query<JournalPage, { page: number }>({
      query: ({ page }) => ({ url: SYNC_URLS.journal, params: { page, pageSize: PAGE_SIZE }, decode: decodeJournal }),
      providesTags: [JOURNAL],
    }),
  }),
})

/** Теги, которые снимаются, когда фоновая задача закончилась: что применилось, уходит из списков само. */
export const SYNC_TAGS_AFTER_JOB = AFTER_CHANGE

export const {
  syncSummary,
  syncDivergences,
  syncDivergenceCount,
  syncCreateTemplate,
  syncApply,
  syncApplyRetry,
  syncJob,
  syncIgnores,
  syncIgnore,
  syncUnignore,
  syncInit,
  syncInitConfirm,
  syncPriceMaps,
  syncPriceMapsSave,
  syncPriceRulesSave,
  syncCheck,
  syncSchedule,
  syncScheduleSave,
  syncJournal,
} = syncApi.endpoints
